import React, { useEffect, useState } from 'react';
import { X, Play, Calendar, Clock } from 'lucide-react';
import { useDetail } from '../context/DetailContext';
import useCanPlayFree from '../hooks/useCanPlayFree';
import Paywall from './Paywall';

const FreeMovieModal = () => {
  const { freeMovie: movie, closeFree, freeAutoplay } = useDetail();
  const canPlay = useCanPlayFree();
  const [playing, setPlaying] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    setPlaying(false);
    if (!movie) return undefined;
    document.body.style.overflow = 'hidden';
    if (freeAutoplay) (canPlay ? setPlaying(true) : setPaywall(true));
    const onKey = (e) => { if (e.key === 'Escape') closeFree(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [movie, freeAutoplay]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!movie) return null;
  const play = () => (canPlay ? setPlaying(true) : setPaywall(true));

  return (
    <div data-testid="free-movie-modal" className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto bg-black/85 backdrop-blur-sm">
      <div className="modal-in relative w-full max-w-5xl bg-[#0e0e14] rounded-2xl overflow-hidden border border-white/10">
        <button data-testid="free-movie-close-btn" onClick={closeFree} className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"><X size={20} /></button>
        <div className="relative aspect-video w-full bg-black overflow-hidden">
          {playing ? (
            <video data-testid="free-movie-player" className="absolute inset-0 w-full h-full bg-black" src={movie.video_url} controls autoPlay playsInline />
          ) : (
            <>
              <img src={movie.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50" />
              <img src={movie.thumbnail} alt={movie.title} className="absolute right-10 top-1/2 -translate-y-1/2 h-3/4 rounded-xl shadow-2xl hidden md:block" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e14] via-[#0e0e14]/50 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-10 max-w-xl">
                <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-emerald-500 text-black">FREE MOVIE · PUBLIC DOMAIN</span>
                <h2 data-testid="free-movie-title" className="font-display text-4xl md:text-6xl text-white leading-none mt-3">{movie.title}</h2>
                <button data-testid="free-movie-play-btn" onClick={play} className="tv-focusable mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90">
                  <Play size={18} className="fill-black" /> Play Movie
                </button>
              </div>
            </>
          )}
        </div>
        <div className="p-8 md:p-10">
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
            {movie.year && <span className="flex items-center gap-1"><Calendar size={14} /> {movie.year}</span>}
            <span className="flex items-center gap-1"><Clock size={14} /> {Math.round(movie.duration_sec / 60)} min</span>
          </div>
          {movie.description && <p className="mt-3 text-white/85 leading-relaxed text-[15px] line-clamp-6">{movie.description}</p>}
          <p className="mt-4 text-white/40 text-xs">Public-domain film courtesy of the Internet Archive.</p>
        </div>
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} title="Subscribe to watch Free Movies" />
    </div>
  );
};

export default FreeMovieModal;
